import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { useCurrentBoxId } from "@/modules/core/store/useCurrentSpaceManagerStore"
import { cn } from "@/modules/lib/utils"
import { word } from "@modules/core/utils/i18n"
import { useMySpaceNavigate } from "@modules/navigation/hooks/useMySpaceNavigate"
import { Path } from "@modules/navigation/routes"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { ProgramBundlePriceInProgramDetailsCard } from "@modules/programBundle/components/programBundlePriceInProgramDetailsCard"
import { ProgramBundleDoubleSubscriptionInformation } from "@modules/programs/components/programBundleDoubleSubscriptionInformation"
import { ProgramPriceCard } from "@modules/programs/components/programPriceCard"
import { ProgramSubscriptionCard } from "@modules/programs/components/programSubscriptionCard"
import { ProgressionRate } from "@modules/programs/components/progressionRate"
import { useProgramRights } from "@modules/programs/hooks/useProgramRights"
import { useProgram } from "@modules/programs/hooks/usePrograms"
import { useProgramSidebar } from "@modules/programs/hooks/useProgramSidebar"
import {
  ProgramStatus,
  ProgramSubscriptionStatus,
  ProgramType,
  SharedCoachStatus,
} from "@modules/programs/types/programTypes"
import { SharedCoachRequest } from "@modules/sharedCoach/components/sharedCoachRequest"
import { SharedCoachView } from "@modules/sharedCoach/components/sharedCoachView"
import { FillRate } from "@modules/ui/components/fillRate"
import { HURichTextView } from "@modules/ui/components/richText/huRichTextView"
import { formatTextToRichText, isRichTextLongerThan } from "@modules/ui/components/richText/richTextUtils"
import { useMemo } from "react"
import { useLocation } from "react-router"

type ProgramInfoDetailsProps = {
  programId: string
  editionAllowed: boolean
  className?: string
} & React.ComponentProps<"div">

export function ProgramInfoDetails({ programId, editionAllowed, className, ...props }: ProgramInfoDetailsProps) {
  const { navigateToProgramEdition } = useProgramSidebar()
  const mySpaceNavigate = useMySpaceNavigate()
  const location = useLocation()
  const boxId = useCurrentBoxId()
  const { isAuthor, isAcceptedSharedCoach, sharedCoach } = useProgramRights(programId)
  const { data: program } = useProgram(programId)

  const isProgramLocation = useMemo(
    () =>
      program.type === ProgramType.ONOFF
        ? location.pathname.includes(Path.MySpace.ProgramOnOffDetails(program.id ?? ""))
        : location.pathname.includes(Path.MySpace.ProgramDetails(program.id ?? "")),
    [program.id, program.type, location.pathname],
  )
  const shouldDisplayFillRate = program.type === ProgramType.ONOFF && isAuthor && program.status === ProgramStatus.DRAFT

  const shouldDisplayProgressionRate =
    program.type === ProgramType.ONOFF && !isAuthor && program.status === ProgramStatus.PUBLISHED

  const isProgramSubscribed = useMemo(
    () =>
      program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
      program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE_BUNDLE ||
      program.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE_BUNDLE,
    [program.subscriptionStatus],
  )

  const isSubscribedInProgramBundle = useMemo(
    () =>
      (program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE ||
        program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE_BUNDLE ||
        program.subscriptionStatus === ProgramSubscriptionStatus.INACTIVE_BUNDLE) &&
      "programBundle" in program &&
      program.programBundle,
    [program.subscriptionStatus, program.programBundle],
  )

  const isSubscribedInBothProgramAndBundle = useMemo(
    () => program.subscriptionStatus === ProgramSubscriptionStatus.ACTIVE && !!program.programBundle,
    [program.subscriptionStatus, program.programBundle, program.type],
  )

  const onViewProgramDetailsClick = () => {
    mySpaceNavigate(
      program.type === ProgramType.ONOFF
        ? Path.MySpace.ProgramOnOffDetails(program.id)
        : Path.MySpace.ProgramDetails(program.id),
    )
  }

  const onEditProgramClick = () => {
    navigateToProgramEdition(program.type, program)
  }

  const richTextDescription = useMemo(() => formatTextToRichText(program.description, true), [program.description])

  return (
    <div className={cn("flex w-full flex-col gap-4 px-5 pt-5 pb-5", className)} {...props}>
      <SharedCoachRequest programId={program.id} currentSharedCoach={sharedCoach} />
      <div className="mb-4">
        <Text
          fontStyle="BM"
          className="line-clamp-4 wrap-break-word whitespace-break-spaces"
          readMore={isRichTextLongerThan({ richText: richTextDescription, numberOfLines: 4, maxLineLength: 70 })}
        >
          <HURichTextView value={richTextDescription} paragrapheFontStyle="BM" />
        </Text>
      </div>

      {shouldDisplayFillRate && <FillRate programId={program.id} editionAllowed={editionAllowed} />}
      {program && isAuthor && <ProgramPriceCard programId={program.id} editionAllowed={false} />}
      {program && !isAuthor && !isProgramSubscribed && !isSubscribedInProgramBundle && (
        <ProgramSubscriptionCard programId={program.id} />
      )}
      {shouldDisplayProgressionRate && <ProgressionRate programId={program.id} editionAllowed={editionAllowed} />}
      {isSubscribedInProgramBundle && program.programBundle && (
        <ProgramBundlePriceInProgramDetailsCard
          programId={program.id}
          programBundleLight={program.programBundle}
          isSubscribedInBothProgramAndBundle={isSubscribedInBothProgramAndBundle ?? false}
        />
      )}
      {program && !isAuthor && isProgramSubscribed && !isSubscribedInProgramBundle && (
        <ProgramSubscriptionCard programId={program.id} />
      )}
      {isSubscribedInBothProgramAndBundle && program.type === ProgramType.ONOFF && (
        <ProgramBundleDoubleSubscriptionInformation programId={program.id} />
      )}

      <Text fontStyle="TS">{word("program.sharedCoach.headCoach")}</Text>
      <div className="flex flex-row items-center justify-center gap-2 overflow-hidden rounded-4xl border-none bg-neutral-100 p-6">
        <ProfileAvatar
          className="size-20"
          profile={{ avatar: program.owner?.avatar ?? null, username: program.owner?.firstname || "" }}
        />
        <div className="flex flex-col overflow-hidden text-center">
          <Text fontStyle="TM" className="overflow-hidden text-ellipsis text-textDefault-caption">
            {program.owner?.firstname + " " + program.owner?.lastname}
          </Text>
          {program.owner?.username && (
            <Text className="overflow-hidden text-ellipsis text-textDefault-caption">
              {"@" + program.owner.username}
            </Text>
          )}
        </div>
      </div>
      {!sharedCoach &&
        program.sharedCoaches &&
        program.sharedCoaches.length > 0 &&
        (program.sharedCoaches.some((coach) => coach.status === SharedCoachStatus.ACCEPTED) || isAuthor) && (
          <>
            <Text fontStyle="TS">{word("program.sharedCoach.title")}</Text>
            <div className="flex flex-col gap-3 rounded-4xl border-0 bg-neutral-100 p-5">
              {program.sharedCoaches.map((coach, index) => (
                <SharedCoachView key={index} profile={coach.user} status={coach.status} isAuthor={isAuthor} />
              ))}
            </div>
          </>
        )}
      {isAuthor && editionAllowed && (
        <Button variant="primary" size="lg" onClick={onEditProgramClick}>
          {word("program.details.modify.button")}
        </Button>
      )}
      {(isProgramSubscribed || isAuthor || isAcceptedSharedCoach) && !isProgramLocation && !boxId && (
        <Button variant="primary" size="lg" onClick={onViewProgramDetailsClick}>
          {word("planning.seeProgram")}
        </Button>
      )}
    </div>
  )
}
